import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import { SelectOption, SelectProps } from '@/types';

import styles from './styles.module.scss';

import DownAngleIcon from '@/assets/icons/down-angle.svg?react';

export const Select = <T,>({ label, options, value, onChange }: SelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const selectRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  const defaultOption: SelectOption<T> = useMemo(() => ({ label: 'All', value: null }), []);
  const selectOptions = useMemo(() => [defaultOption, ...options], [defaultOption, options]);

  const handleSelect = useCallback(
    (selectedOption: SelectOption<T>) => {
      setOpen(false);

      if (selectedOption.value === defaultOption.value) {
        onChange(null);
        return;
      }

      onChange(selectedOption);
    },
    [onChange, defaultOption],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <button
      className={styles.wrapper}
      onClick={() => setOpen(!open)}
      ref={selectRef}
      id={id}
      type="button"
      role="combobox"
      aria-label="select button"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls="select-dropdown"
    >
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div className={styles.select}>
        <div className={styles.value}>{value?.label ?? defaultOption.label}</div>
        <DownAngleIcon className={clsx(styles.icon, { [styles.open]: open })} />
      </div>
      {open && (
        <ul className={styles.optionsList} id="select-dropdown" role="listbox" aria-labelledby={id}>
          {selectOptions.map((option) => (
            <li
              key={`${label}:${option.value ?? 'default'}`}
              className={clsx(styles.option, {
                [styles.selected]: (value?.value ?? null) === option.value,
              })}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </button>
  );
};
